import {
  Component,
  Inject,
  PLATFORM_ID,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SpinService } from '../../../../services/spinservice';
import confetti from 'canvas-confetti';
import { CartService } from '../../../../services/cart';
import { OrdersService } from '../../../../services/orders';

@Component({
  selector: 'app-luckywheel',
  templateUrl: './luckywheel.component.html',
  styleUrls: ['./luckywheel.component.scss']
})
export class LuckywheelComponent implements OnChanges, OnInit, OnDestroy {
  @Input() userId!: any;
  @Output() removeLuckyWheel = new EventEmitter<void>();

  isAr = false;
  spinning = false;
  resultText = '';
  closing = false;
  show = true;
  showFloatingChip = false;
  rewardProduct: any = null;
  previewProducts: any[] = [];
  hostHidden = false;
  private isBrowser: boolean;
  winAudio!: HTMLAudioElement;

  flyingX = 50;
  flyingY = 50;
  flyingScale = 1;
  flyingRotate = 0;

  flyingGiftIcon = '/assets/e-commerce/icons/77.webp';
  private moveTimer: any = null;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private spinService: SpinService,
    private _orderService: OrdersService,
    private cartService: CartService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.winAudio = new Audio('/assets/sounds/win.mp3');
      this.winAudio.volume = 0.9;
    }
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.isAr = localStorage.getItem('language') === 'ar';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId']) {
      this.rewardProduct = null;
      this.resultText = '';
      this.loadPreviewProducts();
    }
  }

  ngOnDestroy(): void {
    this.stopFlyingGift();
  }

  loadPreviewProducts(): void {
    this.spinService.getActive(this.userId ?? null).subscribe(res => {
      if (res.preview_products) {
        this.previewProducts = res.preview_products.slice(0, 6);

        if (this.show && !this.rewardProduct) {
          this.startFlyingGift();
        }
      }
    });
  }

  close(): void {
    if (this.spinning) return;

    this.stopFlyingGift();
    this.closing = true;

    setTimeout(() => {
      this.show = false;
      this.closing = false;
      this.hostHidden = true;
      this.showFloatingChip = false;
      this.removeLuckyWheel.emit();
    }, 300);
  }

  tapFlyingGift(): void {
    if (this.spinning || this.rewardProduct || this.previewProducts.length < 6) return;

    this.spinning = true;
    this.resultText = this.isAr ? 'جاري تحديد الجائزة...' : 'Checking your prize...';
    this.stopFlyingGift();

    this.spinService.spin(this.userId ?? null).subscribe({
      next: (res) => {
        if (!res.product) {
          this.spinning = false;
          this.resultText = this.isAr ? '⚠️ لا توجد جائزة الآن' : '⚠️ No reward available right now';
          this.startFlyingGift();
          return;
        }

        this.rewardProduct = res.product;
        this.resultText = this.isAr
          ? `مبروك! ${this.getProductName(res.product)}`
          : `Congratulations! ${this.getProductName(res.product)}`;

        this.spinning = false;
        this.addFreeProductToCart(res.product.id);
        this.playWinSound();
        this.fireConfetti();
      },
      error: (err) => {
        this.spinning = false;

        this.resultText =
          err.status === 403
            ? this.isAr
              ? '❌ لا يمكنك المحاولة مرة أخرى'
              : '❌ You cannot try again'
            : this.isAr
              ? '⚠️ حدث خطأ، حاول لاحقًا'
              : '⚠️ An error occurred, please try again later';

        if (err.status !== 403) {
          this.startFlyingGift();
        }
      }
    });
  }

  startFlyingGift(): void {
    if (!this.isBrowser || !this.show || this.rewardProduct || this.previewProducts.length < 1) return;

    this.stopFlyingGift();
    this.moveGiftRandomly();

    this.moveTimer = setInterval(() => {
      if (!this.spinning && !this.rewardProduct && this.show) {
        this.moveGiftRandomly();
      }
    }, 850);
  }

  stopFlyingGift(): void {
    if (this.moveTimer) {
      clearInterval(this.moveTimer);
      this.moveTimer = null;
    }
  }

  moveGiftRandomly(): void {
    this.flyingX = this.randomBetween(15, 85);
    this.flyingY = this.randomBetween(18, 74);
    this.flyingScale = this.randomBetween(0.92, 1.14);
    this.flyingRotate = this.randomBetween(-18, 18);
  }

  randomBetween(min: number, max: number): number {
    return +(Math.random() * (max - min) + min).toFixed(2);
  }

  playWinSound(): void {
    if (!this.isBrowser || !this.winAudio) return;

    this.winAudio.currentTime = 0;
    this.winAudio.play().catch(() => { });
  }

  fireConfetti(): void {
    confetti({
      particleCount: 200,
      spread: 100,
      origin: { x: 0.5, y: 0.45 }
    });

    setTimeout(() => {
      confetti({
        particleCount: 120,
        spread: 140,
        origin: { x: 0.5, y: 0.45 }
      });
    }, 400);
  }

  addFreeProductToCart(productId: number): void {
    this._orderService.getByIds([productId]).subscribe((data: any) => {
      const product = { ...(data as any[])[0] };
      product.is_free_reward = true;

      this.cartService.addToCart(product);

      setTimeout(() => {
        this.close();
      }, 5000);
    });
  }

  getProductName(product: any): string {
    if (!product) return '';

    return this.isAr
      ? (product.name_ar || product.name || product.name_en || '')
      : (product.name_en || product.name || product.name_ar || '');
  }

  transformImageUrl(url: string | null): string {
    if (!url) return '';

    if (url.includes('/storage/app/public/')) {
      return url;
    }

    return url.replace('/storage/images/', '/storage/app/public/images/');
  }
}
